import { useCallback, useMemo } from "react";
import type { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { formatFileSize } from "src/helpers/formatFileSize";
import type {
  WorkflowInputFile,
  WorkflowRunCore,
} from "../store/createWorkflowRunStore";

/**
 * The display id scheme for one staged input file, shared by the display
 * mapping and the removal dispatch so they can never disagree.
 */
export const getInputFileId = (file: WorkflowInputFile): string =>
  file.id
    ? `artifact-${file.id}`
    : file.sdrId
      ? `sdr-${file.sdrId}`
      : `file-${file.name}`;

/**
 * Staged input files as AttachmentsContainer rows: the display mapping,
 * the pending-artifact overrides, and the first upload error. One
 * implementation for every surface that shows a workflow run's files
 * (proposal generation's RFPViewer and the shared WorkflowInputFilesViewer).
 */
export const useWorkflowInputFilesDisplay = (
  inputFiles: WorkflowInputFile[],
) => {
  // Artifact-fetch state ("finalizing"/"failed") per file id, overriding the
  // row display in AttachmentListItem.
  const pendingArtifactFileIds = useMemo(() => {
    const map = new Map<string, "finalizing" | "failed">();
    inputFiles.forEach((file) => {
      if (file.awaitingArtifact) {
        // Same id scheme as displayFiles so the override always finds its row.
        map.set(getInputFileId(file), file.awaitingArtifact);
      }
    });
    return map;
  }, [inputFiles]);

  const displayFiles: FileUploadState[] = useMemo(
    () =>
      inputFiles.map((file) => {
        const mockFile =
          file.file ||
          new File([], file.name, { type: "application/octet-stream" });
        if (!file.file) {
          Object.defineProperty(mockFile, "size", {
            value: file.size || 0,
            writable: false,
          });
        }
        return {
          file: mockFile,
          id: getInputFileId(file),
          datasetId: file.datasetId,
          sourceDataRecordId: file.sdrId,
          artifactId: file.id,
          uploadStatus: file.uploadStatus || "uploaded",
          uploadProgress: file.uploadProgress ?? 100,
          processingStatus: file.processingStatus || "completed",
          processingProgress: file.processingProgress ?? 100,
          error: file.error,
          sizeFormatted: formatFileSize(file.size || 0),
          isFromDialogueHistory: !!file.id,
        } as FileUploadState;
      }),
    [inputFiles],
  );

  const uploadError = displayFiles.some(
    (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
  )
    ? displayFiles.find(
        (f) => f.uploadStatus === "failed" || f.processingStatus === "failed",
      )?.error || "Upload failed"
    : null;

  return { displayFiles, pendingArtifactFileIds, uploadError };
};

/**
 * Remove one staged file by its display id: decode the id, drop it from
 * inputFiles and selectedArtifacts, and detach it from the upload hook so
 * onFilesChange cannot re-add it. Returns the updated file list so callers
 * with extra bookkeeping (RFPViewer's ChatStore sync) can read the result.
 */
export const removeWorkflowInputFile = (
  state: Pick<
    WorkflowRunCore,
    | "inputFiles"
    | "selectedArtifacts"
    | "setInputFiles"
    | "removeSelectedArtifact"
    | "removeUploadedFile"
  >,
  fileId: string,
): WorkflowInputFile[] => {
  const {
    inputFiles: files,
    selectedArtifacts,
    setInputFiles,
    removeSelectedArtifact,
    removeUploadedFile,
  } = state;

  let updatedFiles = [...files];
  let hookFileId: string | undefined;

  if (fileId.startsWith("artifact-")) {
    const artifactId = parseInt(fileId.replace("artifact-", ""), 10);
    hookFileId = files.find((f) => f.id === artifactId)?.hookFileId;
    updatedFiles = files.filter((f) => f.id !== artifactId);
    // Also drop it from selectedArtifacts so a re-render cannot re-add it.
    removeSelectedArtifact(artifactId);
  } else if (fileId.startsWith("sdr-")) {
    const sdrId = parseInt(fileId.replace("sdr-", ""), 10);
    hookFileId = files.find((f) => f.sdrId === sdrId)?.hookFileId;
    updatedFiles = files.filter((f) => f.sdrId !== sdrId);
    const matching = selectedArtifacts.find((a) => a.sdr_id === sdrId);
    if (matching) {
      removeSelectedArtifact(matching.id);
    }
  } else if (fileId.startsWith("file-")) {
    const fileName = fileId.replace("file-", "");
    hookFileId = files.find((f) => f.name === fileName)?.hookFileId;
    updatedFiles = files.filter((f) => f.name !== fileName);
    // inputFiles stores cleaned filenames, so clean before comparing.
    const matching = selectedArtifacts.find(
      (a) => cleanArtifactFilename(a.filename) === fileName,
    );
    if (matching) {
      removeSelectedArtifact(matching.id);
    }
  }

  setInputFiles(updatedFiles);
  if (hookFileId && removeUploadedFile) {
    removeUploadedFile(hookFileId);
  }
  return updatedFiles;
};

/** Stable removal callback bound to a run store. */
export const useRemoveWorkflowInputFile = (getState: () => WorkflowRunCore) =>
  useCallback(
    (fileId: string) => {
      removeWorkflowInputFile(getState(), fileId);
    },
    [getState],
  );
